import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Printing = render a template into a body-level `.print-area` and call the browser's print dialog.
 * Any printer the OS knows works (thermal 80/58 mm, A4, "Save as PDF"). Templates set their own @page;
 * A4 ones go through A4Page (no browser margins, our own padding on every page).
 */
type Ctx = { print: (node: ReactNode) => Promise<void> }
const PrintContext = createContext<Ctx>({ print: async () => undefined })

export function PrintHost({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<ReactNode>(null)
  const [resolve, setResolve] = useState<(() => void) | null>(null)

  useEffect(() => {
    if (!node) return
    // Two frames so fonts/barcodes render before the dialog opens.
    const id = requestAnimationFrame(() => requestAnimationFrame(() => window.print()))
    const done = () => {
      setNode(null)
      resolve?.()
    }
    window.addEventListener('afterprint', done, { once: true })
    return () => {
      cancelAnimationFrame(id)
      window.removeEventListener('afterprint', done)
    }
  }, [node, resolve])

  const print = useCallback((n: ReactNode) => new Promise<void>((r) => {
    setResolve(() => r)
    setNode(n)
  }), [])

  return (
    <PrintContext.Provider value={{ print }}>
      {children}
      {node && createPortal(<div className="print-area hidden print:block">{node}</div>, document.body)}
    </PrintContext.Provider>
  )
}

export const usePrint = () => useContext(PrintContext).print

const KEY = 'mira.print.receiptWidth'
export type ReceiptWidth = '80' | '58'
/** Receipt paper width is a property of the device's printer, so it's remembered per browser. */
export function receiptWidth(): ReceiptWidth {
  try {
    return localStorage.getItem(KEY) === '58' ? '58' : '80'
  } catch {
    return '80'
  }
}
export function setReceiptWidth(w: ReceiptWidth) {
  try {
    localStorage.setItem(KEY, w)
  } catch {
    /* ignore */
  }
}
